import { useMemo } from "react";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

interface UseStepsChartConfigProps {
  selectedRange: string;
  navigationOffset: number;
  selectedDate?: Date;
  lowChartData: ChartDataPoint[];
}

interface ChartConfig {
  scale: { x: "time" | "linear" };
  domain: { x: [Date, Date] | [number, number]; y: [number, number] };
  tickValues: (Date | number)[];
  tickFormat: (x: Date | number) => string;
  yTickValues?: number[]; // Add Y-axis tick values
}

// Steps typically range from 0 to 3,000,000+ steps per day
const MIN_STEPS = 0;
const MAX_STEPS = 3000000;

export const useStepsChartConfig = ({
  selectedRange,
  navigationOffset,
  selectedDate,
  lowChartData,
}: UseStepsChartConfigProps): ChartConfig => {
  const chartConfig = useMemo(() => {
    const today = new Date();
    // Calculate dynamic Y domain based on actual steps data
    let yDomain: [number, number] = [MIN_STEPS, MAX_STEPS]; // Default range
    let yTickValues: number[] = [
      0, 500000, 1000000, 1500000, 2000000, 2500000, 3000000,
    ]; // Default tick values

    if (lowChartData.length > 0) {
      const allYValues = lowChartData.map((point) => point.y);
      const minY = Math.min(...allYValues);
      const maxY = Math.max(...allYValues);

      // For steps, ensure reasonable range that adapts to data
      if (allYValues.length === 1) {
        // Single data point: create range around it
        const value = allYValues[0];
        const range = Math.max(5000, value * 0.5); // At least 5000 steps range, or 50% of value
        yDomain = [
          Math.max(0, Math.floor(value - range / 2)), // Minimum 0 steps
          Math.min(5000000, Math.ceil(value + range / 2)), // Maximum 5,000,000 steps
        ];
      } else {
        // Multiple data points: add more generous padding to ensure all data is visible
        const dataRange = maxY - minY;
        const yPadding = Math.max(2000, dataRange * 0.25); // At least 2000 steps padding, or 25% of data range
        yDomain = [
          Math.max(0, Math.floor(minY - yPadding)), // Minimum 0 steps
          Math.min(5000000, Math.ceil(maxY + yPadding)), // Maximum 5,000,000 steps
        ];
      }

      // Ensure Y-axis domain is consistent across all time ranges
      // Force minimum range of 5000 steps to prevent tiny ranges and ensure visibility
      const currentRange = yDomain[1] - yDomain[0];
      if (currentRange < 5000) {
        const center = (yDomain[0] + yDomain[1]) / 2;
        yDomain = [
          Math.max(0, Math.floor(center - 2500)),
          Math.min(5000000, Math.ceil(center + 2500)),
        ];
      }

      // Generate reasonable tick values for steps
      const range = yDomain[1] - yDomain[0];
      const step = Math.max(100000, Math.ceil(range / 8)); // At least 100,000 steps between ticks, aim for 8+ ticks
      yTickValues = [];

      // Add safety check to prevent infinite loops
      const maxIterations = 30;
      let iterations = 0;

      for (
        let i = yDomain[0];
        i <= yDomain[1] && iterations < maxIterations;
        i += step
      ) {
        yTickValues.push(i);
        iterations++;
      }

      // Ensure we have at least the min and max values
      if (!yTickValues.includes(yDomain[0])) yTickValues.unshift(yDomain[0]);
      if (!yTickValues.includes(yDomain[1])) yTickValues.push(yDomain[1]);

      // If we still don't have enough values, add more intermediate ones
      if (yTickValues.length < 6) {
        const stepSize = Math.max(50000, Math.floor(range / 6));
        yTickValues = [];
        for (let i = yDomain[0]; i <= yDomain[1]; i += stepSize) {
          yTickValues.push(i);
        }
        // Ensure we have the max value
        if (!yTickValues.includes(yDomain[1])) {
          yTickValues.push(yDomain[1]);
        }
      }

      // Sort tick values to ensure proper order
      yTickValues.sort((a, b) => a - b);
    }

    switch (selectedRange) {
      case "1d": {
        // Show every 2 hours: 12am, 2am, 4am, 6am, 8am, 10am, 12pm, 2pm, 4pm, 6pm, 8pm, 10pm
        // Use local timezone for day view

        return {
          scale: { x: "linear" as const },
          domain: {
            x: [0, 23] as [number, number],
            y: yDomain,
          },
          tickValues: [0, 3, 6, 9, 12, 15, 18, 21],
          tickFormat: (x: Date | number) => {
            return String(x);
          },
          yTickValues,
        };
      }
      case "7d": {
        // Use selectedDate if provided, otherwise fall back to current date + navigation offset
        let baseDate: Date;

        if (selectedDate) {
          baseDate = new Date(selectedDate);
        } else {
          // Fallback to current date + navigation offset
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth();
          const currentDate = today.getDate();
          baseDate = new Date(currentYear, currentMonth, currentDate);
          baseDate.setDate(baseDate.getDate() + navigationOffset);
        }

        const targetYear = baseDate.getFullYear();
        const targetMonth = baseDate.getMonth();
        const targetDate = baseDate.getDate();
        const targetDay = baseDate.getDay();

        // Calculate week start (Monday) and end (Sunday) in UTC timezone
        const weekStart = new Date(
          Date.UTC(targetYear, targetMonth, targetDate - targetDay + 1),
        ); // Monday
        weekStart.setUTCHours(0, 0, 0, 0);

        const weekEnd = new Date(
          Date.UTC(targetYear, targetMonth, targetDate - targetDay + 7),
        ); // Sunday
        weekEnd.setUTCHours(0, 0, 0, 0);

        const weekTicks = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(
            Date.UTC(targetYear, targetMonth, targetDate - targetDay + 1 + i),
          ); // Mon to Sun
          // Set to UTC midnight to avoid timezone edge cases
          date.setUTCHours(0, 0, 0, 0);
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [weekStart, weekEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: weekTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { weekday: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "1m": {
        // Use selectedDate if provided, otherwise fall back to current date + navigation offset
        let baseDate: Date;

        if (selectedDate) {
          baseDate = new Date(selectedDate);
        } else {
          // Fallback to current date + navigation offset
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth();
          const currentDate = today.getDate();
          baseDate = new Date(currentYear, currentMonth, currentDate);
          baseDate.setDate(baseDate.getDate() + navigationOffset);
        }

        const targetYear = baseDate.getFullYear();
        const targetMonth = baseDate.getMonth();

        const monthStart = new Date(
          Date.UTC(targetYear, targetMonth, 1, 0, 0, 0, 0),
        ); // 1st of target month
        const monthEnd = new Date(
          Date.UTC(targetYear, targetMonth + 1, 0, 0, 0, 0, 0),
        ); // Last day of target month

        // Create ticks for every 2 days
        const daysInMonth = monthEnd.getUTCDate();
        const tickCount = Math.ceil(daysInMonth / 2);
        const monthTicks = Array.from({ length: tickCount }, (_, i) => {
          const day = Math.floor(i * 2) + 1;
          const date = new Date(
            Date.UTC(targetYear, targetMonth, day, 0, 0, 0, 0),
          );
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [monthStart, monthEnd] as [Date, Date],
            y: yDomain,
          },
          tickValues: monthTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.getDate().toString();
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "6m": {
        // Show 6 months based on actual data from API
        let targetYear = today.getFullYear();
        let isFirstHalf = true; // Default to Jan-Jun

        // Use data from API to determine the correct 6-month period
        if (lowChartData.length > 0) {
          // Get the year from the first data point
          const firstDataPoint = lowChartData[0];
          if (firstDataPoint.x instanceof Date) {
            targetYear = firstDataPoint.x.getFullYear();
            const firstMonth = firstDataPoint.x.getMonth();
            isFirstHalf = firstMonth <= 5; // Jan-Jun (0-5) or Jul-Dec (6-11)
          }
        } else {
          // Fallback to current date logic
          const currentMonth = today.getMonth();
          isFirstHalf = currentMonth <= 5;
        }

        const sixMonthTicks = Array.from({ length: 6 }, (_, i) => {
          const monthIndex = isFirstHalf ? i : i + 6; // 0-5 for Jan-Jun, 6-11 for Jul-Dec
          const date = new Date(Date.UTC(targetYear, monthIndex, 1, 0, 0, 0)); // 1st of each month in UTC
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [
              new Date(Date.UTC(targetYear, isFirstHalf ? 0 : 6, 1, 0, 0, 0)), // Start of first month (Jan or Jul)
              new Date(Date.UTC(targetYear, isFirstHalf ? 5 : 11, 1, 0, 0, 0)), // End of last month (Jun or Dec)
            ] as [Date, Date],
            y: yDomain,
          },
          tickValues: sixMonthTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { month: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      case "1y": {
        // Show 12 months based on actual data from API
        let targetYear = today.getFullYear();

        // Use data from API to determine the correct year
        if (lowChartData.length > 0) {
          // Get the year from the first data point
          const firstDataPoint = lowChartData[0];
          if (firstDataPoint.x instanceof Date) {
            targetYear = firstDataPoint.x.getFullYear();
          }
        }

        const yearTicks = Array.from({ length: 12 }, (_, i) => {
          const date = new Date(Date.UTC(targetYear, i, 1, 0, 0, 0)); // 1st of each month in UTC
          return date;
        });

        return {
          scale: { x: "time" as const },
          domain: {
            x: [
              new Date(Date.UTC(targetYear, 0, 1, 0, 0, 0)), // January 1st in UTC
              new Date(Date.UTC(targetYear, 11, 31, 0, 0, 0)), // December 31st in UTC
            ] as [Date, Date],
            y: yDomain,
          },
          tickValues: yearTicks,
          tickFormat: (x: Date | number) => {
            if (x instanceof Date) {
              return x.toLocaleDateString("en-US", { month: "short" });
            }
            return String(x);
          },
          yTickValues,
        };
      }
      default: {
        // Default to day view
        let baseDate: Date;

        if (selectedDate) {
          baseDate = new Date(selectedDate);
        } else {
          baseDate = new Date(today);
          baseDate.setDate(baseDate.getDate() + navigationOffset);
        }
        baseDate.setHours(0, 0, 0, 0);

        return {
          scale: { x: "linear" as const },
          domain: {
            x: [0, 23] as [number, number],
            y: yDomain,
          },
          tickValues: [0, 3, 6, 9, 12, 15, 18, 21],
          tickFormat: (x: Date | number) => {
            return String(x);
          },
          yTickValues,
        };
      }
    }
  }, [selectedRange, navigationOffset, selectedDate, lowChartData]);

  return chartConfig;
};
